<template>
  <div>
    <!-- Huecos de cobertura de esta empresa -->
    <wz-alert v-if="gaps.length" variant="warning" class="mb-3">
      <strong>Roles sin nadie asignado:</strong>
      <ul class="mt-1 mb-0">
        <li v-for="g in gaps" :key="g.roleId">
          {{ g.roleName }} — lo exigen: {{ g.documentTypes.join(', ') }}
        </li>
      </ul>
    </wz-alert>

    <!-- Quién ocupa cada rol -->
    <wz-card title="Roles de firma">
      <p class="text-muted text-sm mb-3">
        Los tipos de documento piden un rol, no una persona. Cuando cambie quién ocupa el cargo, se reasigna aquí una
        vez y todos los documentos lo toman.
        <router-link :to="{ name: 'signer-role-list' }">Administrar roles</router-link>
      </p>
      <wz-table>
        <thead>
          <tr>
            <th>Rol</th>
            <th>Tipo</th>
            <th>Lo ocupa</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in assignments" :key="a.roleId">
            <td>{{ a.roleName }} <small class="text-muted"><code>{{ a.roleCode }}</code></small></td>
            <td>{{ a.standard ? 'Estándar' : 'Propio' }}</td>
            <td style="min-width: 240px;">
              <wz-select v-model="a.signerId" :items="activeSignerOptions" defaultOption="Nadie asignado"
                         @change="onAssign(a)" />
            </td>
            <td>
              <wz-button v-if="a.signerId" variant="light" size="sm" @click="onUnassign(a)">Quitar</wz-button>
            </td>
          </tr>
        </tbody>
      </wz-table>
    </wz-card>

    <!-- Personas que firman por la empresa -->
    <wz-card title="Firmantes" class="mt-3">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input v-model="form.fullName" label="Nombre completo" required />
        </div>
        <div class="w-full md:w-1/6 mb-3 px-2">
          <wz-input v-model="form.documentType" label="Tipo de doc." placeholder="DNI" />
        </div>
        <div class="w-full md:w-1/6 mb-3 px-2">
          <wz-input v-model="form.documentNumber" label="N.° de documento" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input v-model="form.roleTitle" label="Cargo" placeholder="Gerente General" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input v-model="form.registryEntry" label="Partida registral de sus poderes" />
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <wz-button v-if="editingId" variant="light" @click="resetForm">Cancelar</wz-button>
        <wz-button variant="primary" class="ml-2" :disabled="!form.fullName" @click="saveSigner">
          {{ editingId ? 'Guardar cambios' : 'Agregar firmante' }}
        </wz-button>
      </div>

      <p class="text-muted text-sm mt-3 mb-0">
        Para firmar en Crystal, cada firmante necesita su usuario vinculado. La persona copia su ID desde «Firmas por firmar»
        y otra persona con el permiso de firmas lo pega aquí: nadie se vincula a sí mismo. El correo y la imagen de firma los
        carga cada firmante desde esa misma pantalla.
      </p>

      <wz-table class="mt-3">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Documento</th>
            <th>Cargo</th>
            <th>Partida</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="signers.length === 0">
            <td colspan="6" class="text-center text-muted py-4">Sin firmantes registrados.</td>
          </tr>
          <template v-for="s in signers">
          <tr :key="s.id">
            <td>{{ s.fullName }}</td>
            <td>{{ [s.documentType, s.documentNumber].filter(Boolean).join(' ') || '—' }}</td>
            <td>{{ s.roleTitle || '—' }}</td>
            <td>{{ s.registryEntry || '—' }}</td>
            <td>
              <wz-badge :variant="s.status === 1 ? 'success' : 'secondary'" pill>
                {{ s.status === 1 ? 'Activo' : 'Inactivo' }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown :items="signerActions(s)" placement="left" @click="handleSignerAction(s, $event)" />
            </td>
          </tr>
          <tr :key="s.id + '-link'">
            <td colspan="6">
              <div class="mb-2">
                <wz-badge :variant="s.keycloakUserId ? 'success' : 'warning'" pill class="mr-1">{{ s.keycloakUserId ? 'Usuario vinculado' : 'Sin usuario' }}</wz-badge>
                <wz-badge :variant="s.contactValidated ? 'success' : 'warning'" pill class="mr-1">{{ s.contactValidated ? 'Correo validado' : 'Correo sin validar' }}</wz-badge>
                <wz-badge :variant="s.hasSignatureImage ? 'success' : 'warning'" pill>{{ s.hasSignatureImage ? 'Imagen de firma cargada' : 'Sin imagen de firma' }}</wz-badge>
              </div>
              <div class="flex flex-wrap items-end gap-2">
                <div class="flex-1" style="min-width: 280px;">
                  <wz-input v-model="links[s.id]" label="ID de usuario" placeholder="Pega aquí el ID que le aparece a la persona" />
                </div>
                <wz-button variant="primary" size="sm" class="mb-1" :disabled="!linkChanged(s)" @click="saveLink(s)">Guardar usuario</wz-button>
                <wz-button v-if="s.keycloakUserId" variant="light" size="sm" class="mb-1" @click="unlink(s)">Desvincular</wz-button>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </wz-table>
    </wz-card>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { SignerService } from '@/core/services'

const emptyForm = () => ({ fullName: null, documentType: 'DNI', documentNumber: null, roleTitle: null, registryEntry: null })

export default {
  name: 'CompanySignersSection',
  props: {
    companyId: { type: Number, required: true },
  },
  data() {
    return {
      signers: [],
      assignments: [],
      gaps: [],
      form: emptyForm(),
      editingId: null,
      // Lo que está escrito en cada caja de usuario, por firmante.
      links: {},
    }
  },
  computed: {
    activeSignerOptions() {
      return this.signers.filter(s => s.status === 1).map(s => ({ key: s.id, label: s.fullName }))
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    async load() {
      try {
        const [signers, assignments, gaps] = await Promise.all([
          SignerService.listSigners(this.companyId),
          SignerService.assignments(this.companyId),
          SignerService.coverage(this.companyId),
        ])
        this.signers = signers.payload || []
        this.links = this.signers.reduce((all, s) => ({ ...all, [s.id]: s.keycloakUserId || '' }), {})
        this.assignments = assignments.payload || []
        this.gaps = gaps.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los firmantes')
      }
    },
    async onAssign(a) {
      try {
        if (a.signerId) await SignerService.assign(this.companyId, a.roleId, a.signerId)
        else await SignerService.unassign(this.companyId, a.roleId)
        Alert.success('Rol actualizado')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al asignar el rol')
      }
      this.load()
    },
    async onUnassign(a) {
      a.signerId = null
      this.onAssign(a)
    },
    async saveSigner() {
      try {
        if (this.editingId) await SignerService.updateSigner(this.editingId, this.form)
        else await SignerService.createSigner(this.companyId, this.form)
        Alert.success('Firmante guardado')
        this.resetForm()
        this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el firmante')
      }
    },
    resetForm() {
      this.form = emptyForm()
      this.editingId = null
    },
    linkChanged(s) {
      const value = (this.links[s.id] || '').trim()
      return !!value && value !== s.keycloakUserId
    },
    saveLink(s) {
      const save = () => this.sendLink(s, this.links[s.id].trim(), 'Usuario vinculado')
      if (!s.keycloakUserId) return save()
      Alert.dialog('Cambiar usuario', `${this.linkWarning(s)} ¿Continuar?`, save)
    },
    unlink(s) {
      Alert.dialog('Desvincular usuario', `${this.linkWarning(s)} ¿Continuar?`, () => this.sendLink(s, '', 'Usuario desvinculado'))
    },
    linkWarning(s) {
      return `${s.fullName} pierde su correo validado y su imagen de firma: quien quede vinculado tendrá que cargarlos otra vez. Al correo anterior le llega un aviso.`
    },
    async sendLink(s, keycloakUserId, success) {
      try {
        await SignerService.linkUser(s.id, keycloakUserId)
        Alert.success(success)
        this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al vincular el usuario')
      }
    },
    signerActions(s) {
      return [
        { key: 'edit', label: 'Editar' },
        { key: 'status', label: s.status === 1 ? 'Desactivar' : 'Activar' },
      ]
    },
    async handleSignerAction(s, item) {
      if (item.key === 'edit') {
        this.editingId = s.id
        this.form = {
          fullName: s.fullName, documentType: s.documentType, documentNumber: s.documentNumber,
          roleTitle: s.roleTitle, registryEntry: s.registryEntry,
        }
      }
      if (item.key === 'status') {
        try {
          await SignerService.signerStatus(s.id, s.status === 1 ? 0 : 1)
          this.load()
        } catch (e) {
          Alert.error(e.errors ? e.errors.message : 'Error al cambiar el estado')
        }
      }
    },
  },
}
</script>
